import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { useLocation } from 'wouter';
import {
  useCreateLead,
  useDuplicateCheck,
  BUSINESS_MODELS,
  LEAD_SOURCES,
  type DuplicateMatch,
  type LeadSource,
} from '@/models/leads';
import { IntlPhoneInput } from '@/components/IntlPhoneInput';
import Link from '@/components/Link';

export function CreateLeadModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const [, setLocation] = useLocation();
  const mutate = useCreateLead();
  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      whatsapp: '',
      telegram: '',
      source: 'Manual' as LeadSource | null,
      sourceDetail: '',
      request: '',
      extendedMessage: '',
      website: '',
      businessModel: null as string | null,
    },
    validate: {
      firstName: (v, values) =>
        !v && !values.lastName ? 'First or last name required' : null,
      lastName: (v, values) =>
        !v && !values.firstName ? 'First or last name required' : null,
      source: (v) => (!v ? 'Source is required' : null),
    },
  });

  const [debounced] = useDebouncedValue(
    {
      email: form.values.email.trim(),
      phone: form.values.phone.trim(),
      firstName: form.values.firstName.trim(),
      lastName: form.values.lastName.trim(),
    },
    400,
  );
  const dup = useDuplicateCheck(debounced);
  const exact = dup.data?.exact ?? [];
  const similar = dup.data?.similar ?? [];

  const submit = form.onSubmit(async (values) => {
    if (!values.email && !values.phone) {
      form.setFieldError('email', 'Email or phone required');
      form.setFieldError('phone', 'Email or phone required');
      return;
    }
    if (exact.length > 0) return;
    const { lead } = await mutate.mutateAsync({
      ...values,
      source: values.source!,
    });
    onClose();
    setLocation(`~/leads/${lead.id}`);
  });

  const formatMatch = (m: DuplicateMatch) => {
    const name =
      [m.firstName, m.lastName].filter(Boolean).join(' ') || '(no name)';
    const contact =
      [m.email, m.phone].filter(Boolean).join(' — ') || '(no contact)';
    const label = `${m.entity === 'lead' ? 'Lead' : 'Contact'} #${m.id} — ${name} — ${contact}`;
    const to = m.entity === 'lead' ? `~/leads/${m.id}` : `~/contacts/${m.id}`;
    return (
      <Text key={`${m.entity}-${m.id}`} size="sm">
        <Link to={to} onClick={onClose}>
          {label}
        </Link>
      </Text>
    );
  };

  return (
    <Modal opened={opened} onClose={onClose} title="New lead" size="lg">
      <form onSubmit={submit}>
        <Stack>
          <Group grow>
            <TextInput
              label="First name"
              {...form.getInputProps('firstName')}
            />
            <TextInput label="Last name" {...form.getInputProps('lastName')} />
          </Group>
          <Group grow>
            <TextInput
              label="Email"
              type="email"
              {...form.getInputProps('email')}
            />
            <IntlPhoneInput
              label="Phone"
              value={form.values.phone}
              onChange={(v) => form.setFieldValue('phone', v)}
              error={form.errors.phone as string}
            />
          </Group>
          <Group grow>
            <IntlPhoneInput
              label="WhatsApp"
              value={form.values.whatsapp}
              onChange={(v) => form.setFieldValue('whatsapp', v)}
              error={form.errors.whatsapp as string}
            />
            <TextInput label="Telegram" {...form.getInputProps('telegram')} />
          </Group>
          <Group grow>
            <Select
              label="Source"
              data={LEAD_SOURCES}
              searchable
              {...form.getInputProps('source')}
            />
            <TextInput
              label="Source detail"
              {...form.getInputProps('sourceDetail')}
            />
          </Group>
          <Group grow>
            <TextInput label="Website" {...form.getInputProps('website')} />
            <Select
              label="Business model"
              data={BUSINESS_MODELS}
              clearable
              {...form.getInputProps('businessModel')}
            />
          </Group>
          <Textarea
            label="Original request"
            autosize
            minRows={3}
            {...form.getInputProps('request')}
          />
          <Textarea
            label="Notes"
            autosize
            minRows={2}
            {...form.getInputProps('extendedMessage')}
          />
          {exact.length > 0 && (
            <Alert color="red" title="Duplicate found — cannot create">
              {exact.map(formatMatch)}
            </Alert>
          )}
          {exact.length === 0 && similar.length > 0 && (
            <Alert color="yellow" title="Possible duplicates">
              {similar.map(formatMatch)}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={mutate.isPending}
              disabled={exact.length > 0}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
